const HTML_ENTITY_MAP: Record<string, string> = {
    // Basic entities
    quot: '"',
    apos: "'",
    lt: '<',
    gt: '>',
    amp: '&',

    // Whitespace
    nbsp: '\u00A0',

    // Quotes and dashes
    lsquo: '\u2018',
    rsquo: '\u2019',
    ldquo: '\u201C',
    rdquo: '\u201D',
    ndash: '\u2013',
    mdash: '\u2014',

    // Common symbols
    hellip: '\u2026',
    copy: '\u00A9',
    reg: '\u00AE',
    trade: '\u2122',
    deg: '\u00B0',
    times: '\u00D7',
    divide: '\u00F7',

    // Fractions
    frac12: '\u00BD',
    frac14: '\u00BC',
    frac34: '\u00BE',

    // Currency
    euro: '\u20AC',
    pound: '\u00A3',
    yen: '\u00A5',
    cent: '\u00A2',

    // Accented characters (lowercase)
    aacute: '\u00E1',
    eacute: '\u00E9',
    iacute: '\u00ED',
    oacute: '\u00F3',
    uacute: '\u00FA',
    ntilde: '\u00F1',
    agrave: '\u00E0',
    egrave: '\u00E8',
    igrave: '\u00EC',
    ograve: '\u00F2',
    ugrave: '\u00F9',
    acirc: '\u00E2',
    ecirc: '\u00EA',
    icirc: '\u00EE',
    ocirc: '\u00F4',
    ucirc: '\u00FB',
    auml: '\u00E4',
    euml: '\u00EB',
    iuml: '\u00EF',
    ouml: '\u00F6',
    uuml: '\u00FC',
    ccedil: '\u00E7',

    // Accented characters (uppercase)
    Aacute: '\u00C1',
    Eacute: '\u00C9',
    Iacute: '\u00CD',
    Oacute: '\u00D3',
    Uacute: '\u00DA',
    Ntilde: '\u00D1',
    Agrave: '\u00C0',
    Egrave: '\u00C8',
    Igrave: '\u00CC',
    Ograve: '\u00D2',
    Ugrave: '\u00D9',
    Acirc: '\u00C2',
    Ecirc: '\u00CA',
    Icirc: '\u00CE',
    Ocirc: '\u00D4',
    Ucirc: '\u00DB',
    Auml: '\u00C4',
    Euml: '\u00CB',
    Iuml: '\u00CF',
    Ouml: '\u00D6',
    Uuml: '\u00DC',
    Ccedil: '\u00C7',
};

/**
 * Decodes HTML entities in a string to their corresponding characters.
 *
 * Handles three types of HTML entities:
 * 1. Hexadecimal numeric entities (&#xNN;) - handles any Unicode character by code point
 * 2. Decimal numeric entities (&#NNN;) - handles any Unicode character by code point
 * 3. Named entities (&name;) - handles 50+ common entities, leaves unknown ones unchanged
 *
 * Examples:
 * - `&quot;` or `&#34;` or `&#x22;` → `"`
 * - `&amp;` or `&#38;` or `&#x26;` → `&`
 * - `&apos;` or `&#39;` or `&#x27;` → `'`
 * - `&lt;` → `<`, `&gt;` → `>`
 * - `&nbsp;` → non-breaking space
 * - `&copy;` → `©`, `&trade;` → `™`
 * - `&euro;` → `€`, `&pound;` → `£`
 * - `&aacute;` → `á`, `&ntilde;` → `ñ`
 * - `&unknownEntity;` → `&unknownEntity;` (unchanged)
 *
 * @param text - The string containing HTML entities to decode
 * @returns The decoded string with entities replaced by their corresponding characters
 */
// String.fromCodePoint throws RangeError above U+10FFFF; leave malformed entities untouched.
const safeFromCodePoint = (codePoint: number, original: string): string => {
    return codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : original;
};

export const decodeHtmlEntities = (text: string): string => {
    return (
        text
            // First, handle numeric entities (decimal: &#123; and hex: &#xAB;)
            // This handles ANY numeric entity, not just common ones
            // Use String.fromCodePoint instead of fromCharCode to handle emojis and characters above U+FFFF
            .replace(/&#x([0-9A-Fa-f]+);/g, (match: string, hex: string) =>
                safeFromCodePoint(parseInt(hex, 16), match),
            )
            .replace(/&#(\d+);/g, (match: string, dec: string) =>
                safeFromCodePoint(parseInt(dec, 10), match),
            )
            // Then handle named entities using our map
            // Allow alphanumeric to handle entities like &frac12;, &frac14;, etc.
            .replace(/&([a-zA-Z0-9]+);/g, (match: string, entity: string) => {
                return HTML_ENTITY_MAP[entity] || match; // Return original if not in map
            })
    );
};
